// Board map (feature catalogue B2): the whole board small – frames, cards as dots, the visible
// part as a rectangle. Click or drag in the map to look there. Drawn with Canvas 2D, only when the
// board or the view changes (at most every 100 ms for board changes).
import type { Cam } from "./camera";
import { CARD_STRIDE } from "./engine-api";
import { NAVY, STICKY, css } from "./palette";
import type { Store } from "./store";

const W = 220, H = 150, PAD = 8;

export class Minimap {
  private canvas = document.getElementById("lw-map") as HTMLCanvasElement;
  private box = document.getElementById("lw-map-box") as HTMLElement;
  private ctx = this.canvas.getContext("2d")!;
  /** Board rect shown in the map and its scale (board px → map px). */
  private world = { x: 0, y: 0, s: 1 };
  private dirty = true;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private cards: Float32Array = new Float32Array();

  constructor(private store: Store, private view: () => { cam: Cam; w: number; h: number }, private go: (bx: number, by: number) => void) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = W * dpr;
    this.canvas.height = H * dpr;
    this.canvas.style.width = `${W}px`;
    this.canvas.style.height = `${H}px`;
    this.ctx.scale(dpr, dpr);
    store.subscribe((source) => {
      if (source === "view") return;
      this.dirty = true;
      if (!this.timer) this.timer = setTimeout(() => { this.timer = null; this.draw(); }, 100);
    });
    let dragging = false;
    const jump = (e: PointerEvent) => {
      const r = this.canvas.getBoundingClientRect();
      this.go(this.world.x + (e.clientX - r.left) / this.world.s, this.world.y + (e.clientY - r.top) / this.world.s);
    };
    this.canvas.addEventListener("pointerdown", (e) => { dragging = true; this.canvas.setPointerCapture(e.pointerId); jump(e); });
    this.canvas.addEventListener("pointermove", (e) => { if (dragging) jump(e); });
    const stop = () => { dragging = false; };
    this.canvas.addEventListener("pointerup", stop);
    this.canvas.addEventListener("pointercancel", stop);
  }

  get open() { return !this.box.hidden; }

  toggle(on = this.box.hidden) {
    this.box.hidden = !on;
    if (on) { this.dirty = true; this.draw(); }
  }

  /** Redraws (the view rectangle changes with every camera move). */
  draw() {
    if (this.box.hidden) return;
    const { cam, w, h } = this.view();
    const vx = cam.x, vy = cam.y, vw = w / cam.z, vh = h / cam.z;
    if (this.dirty) {
      this.cards = this.store.visible(-1e7, -1e7, 2e7, 2e7).buf;
      this.dirty = false;
    }
    // world = board content plus the view, so the rectangle never leaves the map
    const b = this.store.bounds() ?? [vx, vy, vw, vh];
    const x0 = Math.min(b[0], vx), y0 = Math.min(b[1], vy);
    const x1 = Math.max(b[0] + b[2], vx + vw), y1 = Math.max(b[1] + b[3], vy + vh);
    const s = Math.min((W - 2 * PAD) / Math.max(x1 - x0, 1), (H - 2 * PAD) / Math.max(y1 - y0, 1));
    this.world = { x: x0 - (W / s - (x1 - x0)) / 2, y: y0 - (H / s - (y1 - y0)) / 2, s };
    const tx = (bx: number) => (bx - this.world.x) * s, ty = (by: number) => (by - this.world.y) * s;
    const c = this.ctx;
    c.clearRect(0, 0, W, H);
    // frames
    for (const f of this.store.frames()) {
      const fw = f.cols.reduce((a, col) => a + col.width, 0), fh = f.rows.reduce((a, r) => a + r.height, 0);
      c.fillStyle = "rgba(255,255,255,.9)";
      c.fillRect(tx(f.x), ty(f.y), fw * s, fh * s);
      c.fillStyle = css(NAVY);
      c.fillRect(tx(f.x), ty(f.y), fw * s, Math.max(1.5, (f.rows[0]?.height ?? 60) * s));
      c.strokeStyle = "rgba(0,35,75,.25)";
      c.lineWidth = 1;
      c.strokeRect(tx(f.x) + 0.5, ty(f.y) + 0.5, fw * s - 1, fh * s - 1);
    }
    // images: light boxes
    c.fillStyle = "rgba(0,35,75,.18)";
    for (const i of this.store.images()) c.fillRect(tx(i.x), ty(i.y), Math.max(1.5, i.w * s), Math.max(1.5, i.h * s));
        // cards: their colour, at least a pixel and a half
    const buf = this.cards;
    for (let o = 0; o + CARD_STRIDE <= buf.length; o += CARD_STRIDE) {
      const col = STICKY[buf[o + 4]]?.rgb ?? STICKY[0].rgb;
      c.fillStyle = `rgb(${Math.max(0, col[0] - 40)},${Math.max(0, col[1] - 40)},${Math.max(0, col[2] - 40)})`;
      c.fillRect(tx(buf[o]), ty(buf[o + 1]), Math.max(1.5, buf[o + 2] * s), Math.max(1.5, buf[o + 3] * s));
    }
    // saved view areas: thin outlines (the default one a little stronger)
    for (const v of this.store.views()) {
      c.strokeStyle = v.default ? "rgba(0,35,75,.7)" : "rgba(0,35,75,.35)";
      c.lineWidth = 1;
      c.strokeRect(tx(v.x) + 0.5, ty(v.y) + 0.5, Math.max(2, v.w * s - 1), Math.max(2, v.h * s - 1));
    }
    // the visible part
    c.strokeStyle = "#02BBEE";
    c.lineWidth = 2;
    c.fillStyle = "rgba(2,187,238,.08)";
    c.fillRect(tx(vx), ty(vy), vw * s, vh * s);
    c.strokeRect(tx(vx) + 1, ty(vy) + 1, Math.max(2, vw * s - 2), Math.max(2, vh * s - 2));
  }
}
